import React, { useState, useEffect, useRef, useCallback } from "react";
import { injectIntl } from "react-intl";
import IntlMessages from "../../../helpers/IntlMessages";
import Chart from "chart.js";
import { AddCommaNumber } from "../../../helpers/Utils";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import moment from "moment";
import Switch from "rc-switch";
import "rc-switch/assets/index.css";
import PerfectScrollbar from "react-perfect-scrollbar";

// import annotationPlugin from 'chartjs-plugin-annotation'; // Import plugin annotation
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardText,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Alert,
  ButtonGroup,
  Tooltip,
} from "reactstrap";
import "./BreakEvenAnalysis.css"; // Import CSS cho component

// Chart.js v2 không cần register
// Chart.register(...registerables, annotationPlugin);

const BreakEvenAnalysis = ({ intl }) => {
  const [periodType, setPeriodType] = useState("month"); // day, month, quarter, year
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [useFallback, setUseFallback] = useState(false); // Switch dùng dữ liệu ước tính
  const [tooltipOpen, setTooltipOpen] = useState(false); // Tooltip cho switch
  const [isManualMode, setIsManualMode] = useState(false); // Chế độ nhập thủ công

  const initialInputs = {
    // period: `Tháng ${new Date().getMonth() + 1}/${new Date().getFullYear()}`, // Không dùng field này để display nữa
    currentRevenue: 0,
    currentGrossProfit: null, // null = chưa có dữ liệu
    totalSoldQuantity: 0,
    rentCost: 0,
    fixedSalaryCost: 0,
    equipmentDepreciation: 0,
    managementCost: 0,
    otherFixedCost: 0,
    unitPrice: null, // null = chưa có dữ liệu
    unitVariableCost: null, // null = chưa có dữ liệu
  };

  const [inputs, setInputs] = useState(initialInputs);
  const [results, setResults] = useState({
    totalFixedCost: 0,
    contributionMargin: 0,
    contributionRatio: 0,
    breakEvenUnits: 0,
    breakEvenRevenue: 0,
    detailTable: [],
    calculated: false, // Thêm cờ để biết đã tính toán hay chưa
  });
  const [error, setError] = useState("");
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoDataMeta, setAutoDataMeta] = useState(null);

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const scrollRef = useRef(null);

  const handleWheel = useCallback((e) => {
    if (e.deltaY !== 0) {
      e.currentTarget.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }, []);

  const setScrollRef = useCallback(
    (el) => {
      if (scrollRef.current) {
        const oldContainer = scrollRef.current._container || scrollRef.current;
        if (oldContainer && oldContainer.removeEventListener) {
          oldContainer.removeEventListener("wheel", handleWheel);
        }
      }

      scrollRef.current = el;

      if (scrollRef.current) {
        const container = scrollRef.current._container || scrollRef.current;
        if (container && container.addEventListener) {
          container.addEventListener("wheel", handleWheel, {
            passive: false,
          });
        }
      }
    },
    [handleWheel],
  );

  useEffect(() => {
    return () => {
      if (scrollRef.current) {
        const container = scrollRef.current._container || scrollRef.current;
        if (container && container.removeEventListener) {
          container.removeEventListener("wheel", handleWheel);
        }
      }
    };
  }, [handleWheel]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Xóa dấu chấm phân cách hàng nghìn và chữ đ để lấy giá trị số
    // Lưu ý: Format vi-VN dùng dấu chấm (.) cho hàng nghìn
    const rawValue = value.replace(/\./g, "").replace(/ đ/g, "").trim();
    setInputs({
      ...inputs,
      [name]: parseFloat(rawValue) || 0,
    });
    setError("");
  };

  // Lấy dữ liệu tự động từ hệ thống
  const loadAutoData = async () => {
    setAutoLoading(true);
    setError("");
    try {
      const tokenStr = localStorage.getItem("access_token");
      const res = await axios.get(`${END_POINT}/breakeven/auto-data`, {
        params: {
          period: periodType,
          date: selectedDate,
          useFallback: useFallback, // Gửi flag dùng dữ liệu ước tính
        },
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (res.data.status === 200 && res.data.data) {
        const data = res.data.data;
        console.log("data: ", data);
        setInputs({
          currentRevenue: data.currentRevenue || 0,
          currentGrossProfit: data.currentGrossProfit, // Giữ null nếu backend trả về null
          totalSoldQuantity: data.totalSoldQuantity || 0,
          rentCost: data.rentCost || 0,
          fixedSalaryCost: data.fixedSalaryCost || 0,
          equipmentDepreciation: data.equipmentDepreciation || 0,
          managementCost: data.managementCost || 0,
          otherFixedCost: data.otherFixedCost || 0,
          unitPrice: data.unitPrice, // Giữ null nếu backend trả về null
          unitVariableCost: data.unitVariableCost, // Giữ null nếu backend trả về null
        });
        setAutoDataMeta(res.data.meta);
        // Tự động tính toán ngay sau khi load data
        // Sử dụng data trực tiếp thay vì đợi state update
        const newInputs = {
          currentRevenue: data.currentRevenue || 0,
          currentGrossProfit: data.currentGrossProfit,
          totalSoldQuantity: data.totalSoldQuantity || 0,
          rentCost: data.rentCost || 0,
          fixedSalaryCost: data.fixedSalaryCost || 0,
          equipmentDepreciation: data.equipmentDepreciation || 0,
          managementCost: data.managementCost || 0,
          otherFixedCost: data.otherFixedCost || 0,
          unitPrice: data.unitPrice,
          unitVariableCost: data.unitVariableCost,
        };
        calculateResultsWithData(newInputs);
      } else {
        setError(
          res.data.responseText ||
            intl.formatMessage({ id: "breakeven.error.fetch-fail" }),
        );
      }
    } catch (err) {
      console.error("Load auto data error:", err);
      setError(
        intl.formatMessage({ id: "breakeven.error.connect-fail" }) +
          (err.response?.data?.responseText || err.message),
      );
    } finally {
      setAutoLoading(false);
    }
  };

  const validateInputs = () => {
    if (inputs.unitPrice <= 0) {
      setError(intl.formatMessage({ id: "breakeven.error.price-invalid" }));
      return false;
    }
    if (inputs.unitVariableCost < 0) {
      setError(intl.formatMessage({ id: "breakeven.error.cost-invalid" }));
      return false;
    }
    return true;
  };

  const calculateResults = () => {
    if (!validateInputs()) {
      // Xóa biểu đồ và bảng nếu có lỗi
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      setResults((prev) => ({ ...prev, detailTable: [], calculated: false }));
      return;
    }

    const totalFixedCost =
      inputs.rentCost +
      inputs.fixedSalaryCost +
      inputs.equipmentDepreciation +
      inputs.managementCost +
      inputs.otherFixedCost;

    // Trong chế độ thủ công, tự động cập nhật doanh thu và lãi gộp dựa trên lượng nhập tay
    if (isManualMode) {
      const currentRevenue = inputs.totalSoldQuantity * inputs.unitPrice;
      const totalCOGS = inputs.totalSoldQuantity * inputs.unitVariableCost;
      const currentGrossProfit = currentRevenue - totalCOGS;

      setInputs((prev) => ({
        ...prev,
        currentRevenue,
        currentGrossProfit,
      }));
    }

    // Nếu unitVariableCost >= unitPrice thì không thể tính điểm hòa vốn (biên lợi nhuận âm)
    if (inputs.unitVariableCost >= inputs.unitPrice) {
      setResults({
        totalFixedCost,
        contributionMargin: inputs.unitPrice - inputs.unitVariableCost,
        contributionRatio:
          ((inputs.unitPrice - inputs.unitVariableCost) / inputs.unitPrice) * 100,
        breakEvenUnits: Infinity,
        breakEvenRevenue: Infinity,
        detailTable: [],
        calculated: true,
      });
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      return;
    }

    const contributionMargin = inputs.unitPrice - inputs.unitVariableCost;
    const contributionRatio = (contributionMargin / inputs.unitPrice) * 100;
    const breakEvenUnits = totalFixedCost / contributionMargin;
    const breakEvenRevenue = breakEvenUnits * inputs.unitPrice;
    const detailTable = generateDetailTable(
      totalFixedCost,
      breakEvenUnits,
      inputs.unitPrice,
      inputs.unitVariableCost,
    );

    setResults({
      totalFixedCost,
      contributionMargin,
      contributionRatio,
      breakEvenUnits,
      breakEvenRevenue,
      detailTable,
      calculated: true,
    });

    drawBreakEvenChart(
      totalFixedCost,
      breakEvenUnits,
      inputs.unitPrice,
      inputs.unitVariableCost,
    );
  };

  // Hàm tính toán với dữ liệu truyền vào (dùng cho auto-calculate)
  const calculateResultsWithData = (data) => {
    // Nếu unitPrice hoặc unitVariableCost là null (không có order data)
    // thì set calculated = true để hiển thị empty state
    if (data.unitPrice === null || data.unitVariableCost === null) {
      setResults({
        totalFixedCost: 0,
        contributionMargin: 0,
        contributionRatio: 0,
        breakEvenUnits: 0,
        breakEvenRevenue: 0,
        detailTable: [],
        calculated: true, // true để hiển thị empty state thay vì loading
      });
      return;
    }

    // Validate dữ liệu - nếu không hợp lệ thì reset kết quả
    console.log("calculateResultsWithData:", data);

    // Chỉ kiểm tra unitPrice > 0, bỏ điều kiện unitPrice > unitVariableCost
    // để cho phép hiển thị kết quả dù có thể âm (lỗ)
    if (data.unitPrice <= 0) {
      console.log("unitPrice <= 0, reset results");
      setResults({
        totalFixedCost: 0,
        contributionMargin: 0,
        contributionRatio: 0,
        breakEvenUnits: 0,
        breakEvenRevenue: 0,
        detailTable: [],
        calculated: false,
      });
      return;
    }

    // Nếu unitVariableCost >= unitPrice thì không thể tính điểm hòa vốn (biên lợi nhuận âm)
    // Nhưng vẫn hiển thị kết quả với cảnh báo
    if (data.unitVariableCost >= data.unitPrice) {
      console.log("unitVariableCost >= unitPrice, biên lợi nhuận âm");
      const totalFixedCost =
        data.rentCost +
        data.fixedSalaryCost +
        data.equipmentDepreciation +
        data.managementCost +
        data.otherFixedCost;

      setResults({
        totalFixedCost,
        contributionMargin: data.unitPrice - data.unitVariableCost,
        contributionRatio:
          ((data.unitPrice - data.unitVariableCost) / data.unitPrice) * 100,
        breakEvenUnits: Infinity, // Không thể hòa vốn
        breakEvenRevenue: Infinity,
        detailTable: [],
        calculated: true,
      });
      return;
    }

    const totalFixedCost =
      data.rentCost +
      data.fixedSalaryCost +
      data.equipmentDepreciation +
      data.managementCost +
      data.otherFixedCost;

    const contributionMargin = data.unitPrice - data.unitVariableCost;
    const contributionRatio = (contributionMargin / data.unitPrice) * 100;
    const breakEvenUnits = totalFixedCost / contributionMargin;
    const breakEvenRevenue = breakEvenUnits * data.unitPrice;
    const detailTable = generateDetailTable(
      totalFixedCost,
      breakEvenUnits,
      data.unitPrice,
      data.unitVariableCost,
    );

    setResults({
      totalFixedCost,
      contributionMargin,
      contributionRatio,
      breakEvenUnits,
      breakEvenRevenue,
      detailTable,
      calculated: true,
    });

    drawBreakEvenChart(
      totalFixedCost,
      breakEvenUnits,
      data.unitPrice,
      data.unitVariableCost,
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // Ngăn form submit mặc định
    calculateResults();
  };

  // Hàm tạo dữ liệu bảng chi tiết (cập nhật để nhận thêm params)
  const generateDetailTable = (
    totalFixedCost,
    beUnits,
    unitPrice,
    unitVariableCost,
  ) => {
    const table = [];
    // Điều chỉnh các mốc sản lượng cho hợp lý hơn
    const volumePoints = [
      0,
      Math.max(0, Math.floor(beUnits * 0.25)),
      Math.max(0, Math.floor(beUnits * 0.5)),
      Math.max(0, Math.floor(beUnits * 0.75)),
      Math.ceil(beUnits), // Điểm hòa vốn
      Math.ceil(beUnits * 1.25),
      Math.ceil(beUnits * 1.5),
      Math.ceil(beUnits * 1.75),
    ]
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort((a, b) => a - b); // Loại bỏ trùng lặp và sắp xếp

    if (beUnits === Infinity || isNaN(beUnits)) {
      // Xử lý trường hợp không tính được điểm hòa vốn
      return [];
    }

    volumePoints.forEach((volume) => {
      const revenue = volume * unitPrice;
      const variableCost = volume * unitVariableCost;
      const totalCost = totalFixedCost + variableCost;
      const profit = revenue - totalCost;
      table.push({
        volume,
        revenue,
        variableCost,
        fixedCost: totalFixedCost,
        totalCost,
        profit,
      });
    });
    return table;
  };

  // Hàm vẽ biểu đồ (cập nhật để nhận thêm params, syntax v2)
  const drawBreakEvenChart = (
    totalFixedCost,
    beUnits,
    unitPrice,
    unitVariableCost,
  ) => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    if (!chartRef.current || beUnits === Infinity || isNaN(beUnits)) return; // Không vẽ nếu không có canvas hoặc BEP không hợp lệ

    const maxUnits = Math.max(10, Math.ceil(beUnits * 1.8)); // Đảm bảo có ít nhất 10 đơn vị trên trục x
    const units = Array.from(
      {
        length:
          Math.floor(maxUnits / Math.max(1, Math.floor(maxUnits / 20))) + 1,
      },
      (_, i) => i * Math.max(1, Math.floor(maxUnits / 20)),
    );
    if (units[units.length - 1] < maxUnits && units.length > 1)
      units.push(maxUnits); // Đảm bảo điểm cuối cùng được bao gồm

    const revenueData = units.map((volume) => volume * unitPrice);
    const fixedCostData = units.map(() => totalFixedCost);
    const totalCostData = units.map(
      (volume) => totalFixedCost + volume * unitVariableCost,
    );

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: units,
        datasets: [
          {
            label: intl.formatMessage({ id: "breakeven.chart.revenue" }),
            data: revenueData,
            borderColor: "rgb(75, 192, 192)",
            lineTension: 0.1,
            fill: false,
          },
          {
            label: intl.formatMessage({ id: "breakeven.chart.total-cost" }),
            data: totalCostData,
            borderColor: "rgb(255, 99, 132)",
            lineTension: 0.1,
            fill: false,
          },
          {
            label: intl.formatMessage({ id: "breakeven.chart.fixed-cost" }),
            data: fixedCostData,
            borderColor: "rgb(255, 159, 64)",
            borderDash: [5, 5],
            lineTension: 0.1,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || "";
              if (label) {
                label += ": ";
              }
              if (tooltipItem.yLabel !== undefined) {
                label += formatCurrency(tooltipItem.yLabel);
              }
              return label;
            },
          },
        },
        hover: {
          mode: "nearest",
          intersect: true,
        },
        scales: {
          xAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: intl.formatMessage({
                  id: "breakeven.chart.x-axis",
                }),
              },
              ticks: { beginAtZero: true },
            },
          ],
          yAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: intl.formatMessage({
                  id: "breakeven.chart.y-axis",
                }),
              },
              ticks: {
                beginAtZero: true,
                callback: function (value) {
                  return formatCurrency(value);
                },
              },
            },
          ],
        },
        title: {
          display: true,
          text: intl.formatMessage({ id: "breakeven.chart.title" }),
        },
        legend: {
          position: "top",
        },
      },
    });
  };

  const formatCurrency = (value) => {
    if (isNaN(value) || value === null) return "N/A";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value, digits = 0) => {
    if (isNaN(value) || value === null) return "N/A";
    return new Intl.NumberFormat("vi-VN", {
      maximumFractionDigits: digits,
    }).format(value);
  };

  // Tự động lấy dữ liệu khi vào trang hoặc thay đổi filter (chỉ khi không ở chế độ thủ công)
  useEffect(() => {
    if (!isManualMode) {
      loadAutoData();
    }
  }, [periodType, selectedDate, useFallback, isManualMode]);

  const handlePeriodChange = (type) => {
    setPeriodType(type);
    setSelectedDate(moment().format("YYYY-MM-DD"));
  };

  return (
    <Container fluid className="break-even-analysis">
      <div className="d-flex flex-wrap justify-content-center align-items-center mb-4">
        <ButtonGroup className="shadow-sm d-flex align-items-center flex-wrap">
          <Button
            color="primary"
            outline={periodType !== "day"}
            className="flex-grow-1"
            onClick={() => handlePeriodChange("day")}
            style={{
              minWidth: "80px",
            }}
          >
            <IntlMessages id="breakeven.period.day" />
          </Button>
          <Button
            color="primary"
            outline={periodType !== "month"}
            className="flex-grow-1"
            onClick={() => handlePeriodChange("month")}
            style={{
              minWidth: "80px",
            }}
          >
            <IntlMessages id="breakeven.period.month" />
          </Button>
          <Button
            color="primary"
            outline={periodType !== "quarter"}
            className="flex-grow-1"
            onClick={() => handlePeriodChange("quarter")}
            style={{
              minWidth: "80px",
            }}
          >
            <IntlMessages id="breakeven.period.quarter" />
          </Button>
          <Button
            color="primary"
            outline={periodType !== "year"}
            className="flex-grow-1"
            onClick={() => handlePeriodChange("year")}
            style={{
              minWidth: "80px",
            }}
          >
            <IntlMessages id="breakeven.period.year" />
          </Button>
        </ButtonGroup>

        {/* Switch dùng dữ liệu ước tính khi không có order */}
        <div className="d-inline-block align-middle ml-4">
          <Switch
            id="useFallbackSwitch"
            className="custom-switch custom-switch-primary custom-switch-small"
            checked={useFallback}
            onChange={(checked) => setUseFallback(checked)}
            disabled={autoDataMeta?.hasOrderData === true || isManualMode}
          />
          <Tooltip
            placement="bottom"
            isOpen={tooltipOpen}
            target="useFallbackSwitch"
            toggle={() => setTooltipOpen(!tooltipOpen)}
          >
            {autoDataMeta?.hasOrderData
              ? intl.formatMessage({ id: "breakeven.fallback.has-data" })
              : useFallback
                ? intl.formatMessage({ id: "breakeven.fallback.active" })
                : intl.formatMessage({ id: "breakeven.fallback.inactive" })}
          </Tooltip>
        </div>

        {/* Switch chế độ nhập thủ công */}
        <div className="d-inline-block align-middle ml-4 d-flex align-items-center">
          <span className="mr-2 text-muted" style={{ fontSize: "0.85rem" }}>
            <IntlMessages id="breakeven.mode.manual" defaultMessage="Nhập thủ công" />
          </span>
          <Switch
            id="manualModeSwitch"
            className="custom-switch custom-switch-primary custom-switch-small"
            checked={isManualMode}
            onChange={(checked) => {
              setIsManualMode(checked);
              if (!checked) {
                // Tải lại dữ liệu khi tắt chế độ thủ công
                loadAutoData();
              }
            }}
          />
        </div>
      </div>

      {error && (
        <Alert color="danger" className="mt-3">
          {error}
        </Alert>
      )}

      <Row>
        <Col xxs="12" md="5">
          <Card className="mb-4 shadow-sm">
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <h3 className="h6 mb-3 text-primary">
                  <IntlMessages id="breakeven.fixed-costs.title" />{" "}
                  {autoDataMeta && (
                    <small className="text-muted ms-2">
                      ({autoDataMeta.dateFrom} - {autoDataMeta.dateTo})
                    </small>
                  )}
                </h3>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="rentCost">
                        <IntlMessages id="breakeven.fixed-costs.rent" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="rentCost"
                        id="rentCost"
                        value={isManualMode ? inputs.rentCost : (AddCommaNumber(inputs.rentCost) + " đ")}
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="fixedSalaryCost">
                        <IntlMessages id="breakeven.fixed-costs.salary" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="fixedSalaryCost"
                        id="fixedSalaryCost"
                        value={isManualMode ? inputs.fixedSalaryCost : (AddCommaNumber(inputs.fixedSalaryCost) + " đ")}
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="equipmentDepreciation">
                        <IntlMessages id="breakeven.fixed-costs.depreciation" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="equipmentDepreciation"
                        id="equipmentDepreciation"
                        value={isManualMode ? inputs.equipmentDepreciation : (AddCommaNumber(inputs.equipmentDepreciation) + " đ")}
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="managementCost">
                        <IntlMessages id="breakeven.fixed-costs.management" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="managementCost"
                        id="managementCost"
                        value={isManualMode ? inputs.managementCost : (AddCommaNumber(inputs.managementCost) + " đ")}
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="otherFixedCost">
                    <IntlMessages id="breakeven.fixed-costs.other" />
                  </Label>
                  <Input
                    type={isManualMode ? "number" : "text"}
                    name="otherFixedCost"
                    id="otherFixedCost"
                    value={isManualMode ? inputs.otherFixedCost : (AddCommaNumber(inputs.otherFixedCost) + " đ")}
                    onChange={handleInputChange}
                    disabled={!isManualMode}
                  />
                </FormGroup>

                <h3 className="h6 my-4 text-primary">
                  <IntlMessages id="breakeven.product.title" />
                </h3>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label for="unitPrice">
                        <IntlMessages id="breakeven.product.price" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="unitPrice"
                        id="unitPrice"
                        value={
                          isManualMode
                            ? (inputs.unitPrice ?? "")
                            : (inputs.unitPrice !== null
                                ? AddCommaNumber(inputs.unitPrice) + " đ"
                                : "—")
                        }
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                        title={
                          inputs.unitPrice === null
                            ? intl.formatMessage({
                                id: "breakeven.product.price.tooltip.no-data",
                              })
                            : ""
                        }
                        style={
                          inputs.unitPrice === null
                            ? { fontStyle: "italic", color: "#6c757d" }
                            : {}
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="unitVariableCost">
                        <IntlMessages id="breakeven.product.variable-cost" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="unitVariableCost"
                        id="unitVariableCost"
                        value={
                          isManualMode
                            ? (inputs.unitVariableCost ?? "")
                            : (inputs.unitVariableCost !== null
                                ? AddCommaNumber(inputs.unitVariableCost) + " đ"
                                : "—")
                        }
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                        title={
                          inputs.unitVariableCost === null
                            ? intl.formatMessage({
                                id: "breakeven.product.variable-cost.tooltip.no-data",
                              })
                            : ""
                        }
                        style={
                          inputs.unitVariableCost === null
                            ? { fontStyle: "italic", color: "#6c757d" }
                            : {}
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="profitMargin">
                        <IntlMessages id="breakeven.product.profit-margin" />
                      </Label>
                      <Input
                        type="text"
                        name="profitMargin"
                        id="profitMargin"
                        value={
                          inputs.unitPrice !== null &&
                          inputs.unitVariableCost !== null &&
                          inputs.unitPrice > 0
                            ? (
                                ((inputs.unitPrice - inputs.unitVariableCost) /
                                  inputs.unitPrice) *
                                100
                              ).toFixed(1) + " %"
                            : "—"
                        }
                        disabled
                        title={
                          inputs.unitPrice === null ||
                          inputs.unitVariableCost === null
                            ? intl.formatMessage({
                                id: "breakeven.product.profit-margin.tooltip.no-data",
                              })
                            : intl.formatMessage({
                                id: "breakeven.product.profit-margin.tooltip.desc",
                              })
                        }
                        style={
                          inputs.unitPrice === null ||
                          inputs.unitVariableCost === null
                            ? { fontStyle: "italic", color: "#6c757d" }
                            : {}
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col md="6">
                    <FormGroup>
                      <Label for="totalSoldQuantity">
                        <IntlMessages id="breakeven.product.sold-quantity" defaultMessage="Sản lượng dự kiến (SP)" />
                      </Label>
                      <Input
                        type={isManualMode ? "number" : "text"}
                        name="totalSoldQuantity"
                        id="totalSoldQuantity"
                        value={
                          isManualMode
                            ? inputs.totalSoldQuantity
                            : (formatNumber(inputs.totalSoldQuantity) + " SP")
                        }
                        onChange={handleInputChange}
                        disabled={!isManualMode}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {isManualMode && (
                  <Button
                    color="primary"
                    block
                    className="mt-3"
                    type="submit"
                  >
                    <IntlMessages id="breakeven.button.calculate" defaultMessage="Tính toán điểm hòa vốn" />
                  </Button>
                )}
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col xxs="12" md="7">
          <Card className="mb-4 shadow-sm">
            {/* <CardHeader className="py-2">
              <h2 className="h6 mb-0">Kết Quả Phân Tích</h2>
            </CardHeader> */}
            <CardBody>
              {!results.calculated && !error && (
                <Alert color="info" className="py-2 mb-0">
                  <IntlMessages id="breakeven.loading" />
                </Alert>
              )}
              {/* Empty state khi không có order data */}
              {results.calculated &&
                !error &&
                (inputs.unitPrice === null ||
                  inputs.unitVariableCost === null) && (
                  <div className="text-center py-4">
                    <div className="mb-3">
                      <i
                        className="simple-icon-info"
                        style={{ fontSize: "3rem", color: "#6c757d" }}
                      ></i>
                    </div>
                    <h5 className="text-muted mb-3">
                      <IntlMessages id="breakeven.empty.title" />
                    </h5>
                    <p className="text-muted mb-0">
                      <IntlMessages id="breakeven.empty.desc" />
                    </p>
                    <small className="text-muted d-block mt-2">
                      <IntlMessages id="breakeven.empty.sub" />
                    </small>
                  </div>
                )}
              {/* Hiển thị kết quả khi có đầy đủ dữ liệu */}
              {results.calculated &&
                !error &&
                inputs.unitPrice !== null &&
                inputs.unitVariableCost !== null && (
                  <>
                    {inputs.unitPrice <= inputs.unitVariableCost && (
                      <Alert color="danger" className="py-2 mb-3">
                        <IntlMessages
                          id="breakeven.warning.negative-margin"
                          defaultMessage="Cảnh báo: Biên đóng góp đang âm hoặc bằng 0 (Giá bán ≤ Giá vốn). Sản phẩm đang bán lỗ và không thể đạt điểm hòa vốn!"
                        />
                      </Alert>
                    )}
                    <Row className="mb-1">
                      <Col sm="6" className="mb-2">
                        <Card body className="text-center py-2">
                          <small className="text-muted d-block">
                            <IntlMessages id="breakeven.result.fixed-cost" />
                          </small>
                          <span className="h6 fw-bold text-danger mb-0">
                            {formatCurrency(results.totalFixedCost)}
                          </span>
                        </Card>
                      </Col>
                      <Col sm="6" className="mb-2">
                        <Card
                          body
                          className="text-center py-2"
                          // style={{ backgroundColor: "#e8f5e9" }}
                        >
                          <small className="text-muted d-block">
                            <IntlMessages id="breakeven.result.revenue" />
                          </small>
                          <span className="h6 fw-bold text-success mb-0">
                            {formatCurrency(inputs.currentRevenue)}
                          </span>
                        </Card>
                      </Col>
                    </Row>
                    <Row className="mb-1">
                      <Col sm="6" className="mb-2">
                        <Card
                          body
                          className="text-center py-2"
                          // style={{
                          //   backgroundColor:
                          //     inputs.currentGrossProfit >= 0
                          //       ? "#e8f5e9"
                          //       : "#ffebee",
                          // }}
                        >
                          <small className="text-muted d-block">
                            <IntlMessages id="breakeven.result.gross-profit" />
                          </small>
                          <span
                            className="h6 fw-bold mb-0"
                            style={{
                              color:
                                inputs.currentGrossProfit >= 0
                                  ? "#2e7d32"
                                  : "#c62828",
                            }}
                          >
                            {formatCurrency(inputs.currentGrossProfit)}
                          </span>
                        </Card>
                      </Col>
                      <Col sm="6" className="mb-2">
                        <Card body className="text-center py-2">
                          <small className="text-muted d-block">
                            <IntlMessages id="breakeven.result.sold-quantity" />
                          </small>
                          <span className="h6 fw-bold text-primary mb-0">
                            {formatNumber(inputs.totalSoldQuantity)} SP
                          </span>
                        </Card>
                      </Col>
                    </Row>
                    <Row className="mb-1">
                      <Col sm="6" className="mb-2">
                        <Card body className="text-center py-2 alert-warning">
                          <small className="text-muted d-block">
                            <IntlMessages id="breakeven.result.breakeven-units" />
                          </small>
                          <span className="h6 fw-bolder mb-0">
                            {formatNumber(results.breakEvenUnits)} SP
                          </span>
                          <div className="mt-1">
                            <div className="progress" style={{ height: "6px" }}>
                              <div
                                className={`progress-bar ${
                                  (inputs.totalSoldQuantity /
                                    results.breakEvenUnits) *
                                    100 >=
                                  100
                                    ? "bg-success"
                                    : "bg-warning"
                                }`}
                                role="progressbar"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (inputs.totalSoldQuantity /
                                      results.breakEvenUnits) *
                                      100,
                                  )}%`,
                                }}
                              ></div>
                            </div>
                            <small
                              className={`fw-bold ${
                                (inputs.totalSoldQuantity /
                                  results.breakEvenUnits) *
                                  100 >=
                                100
                                  ? "text-success"
                                  : "text-warning"
                              }`}
                            >
                              {formatNumber(
                                (inputs.totalSoldQuantity /
                                  results.breakEvenUnits) *
                                  100,
                                1,
                              )}
                              %
                            </small>
                          </div>
                        </Card>
                      </Col>
                      <Col sm="6" className="mb-2">
                        <Card body className="text-center py-2 alert-info">
                          <small className="text-muted d-block">
                            <IntlMessages id="breakeven.result.breakeven-revenue" />
                          </small>
                          <span className="h6 fw-bolder mb-0">
                            {formatCurrency(results.breakEvenRevenue)}
                          </span>
                          <div className="mt-1">
                            <div className="progress" style={{ height: "6px" }}>
                              <div
                                className={`progress-bar ${
                                  (inputs.currentRevenue /
                                    results.breakEvenRevenue) *
                                    100 >=
                                  100
                                    ? "bg-success"
                                    : "bg-info"
                                }`}
                                role="progressbar"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (inputs.currentRevenue /
                                      results.breakEvenRevenue) *
                                      100,
                                  )}%`,
                                }}
                              ></div>
                            </div>
                            <small
                              className={`fw-bold ${
                                (inputs.currentRevenue /
                                  results.breakEvenRevenue) *
                                  100 >=
                                100
                                  ? "text-success"
                                  : "text-info"
                              }`}
                            >
                              {formatNumber(
                                (inputs.currentRevenue /
                                  results.breakEvenRevenue) *
                                  100,
                                1,
                              )}
                              %
                            </small>
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    {/* Bảng Chi Tiết Phân Tích Lợi Nhuận */}
                    {results.detailTable.length > 0 && (
                      <div className="mt-3">
                        <h6 className="text-muted mb-2">
                          <IntlMessages id="breakeven.table.title" />
                        </h6>
                        <div
                          className="table-responsive"
                          style={{ maxHeight: "250px" }}
                        >
                          <PerfectScrollbar
                            ref={setScrollRef}
                            options={{
                              suppressScrollY: false,
                              wheelPropagation: false,
                            }}
                          >
                            <Table
                              striped
                              bordered
                              hover
                              size="sm"
                              className="text-center mb-0"
                              style={{ fontSize: "0.8rem" }}
                            >
                              <thead className="sticky-top">
                                <tr>
                                  <th>
                                    <IntlMessages id="breakeven.table.volume" />
                                  </th>
                                  <th>
                                    <IntlMessages id="breakeven.table.revenue" />
                                  </th>
                                  <th>
                                    <IntlMessages id="breakeven.table.variable-cost" />
                                  </th>
                                  <th>
                                    <IntlMessages id="breakeven.table.fixed-cost" />
                                  </th>
                                  <th>
                                    <IntlMessages id="breakeven.table.total-cost" />
                                  </th>
                                  <th>
                                    <IntlMessages id="breakeven.table.profit" />
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {results.detailTable.map((row, index) => (
                                  <tr
                                    key={index}
                                    className={
                                      row.volume ===
                                      Math.ceil(results.breakEvenUnits)
                                        ? "break-even-row fw-bold"
                                        : ""
                                    }
                                  >
                                    <td>{formatNumber(row.volume)}</td>
                                    <td>{formatCurrency(row.revenue)}</td>
                                    <td>{formatCurrency(row.variableCost)}</td>
                                    <td>{formatCurrency(row.fixedCost)}</td>
                                    <td>{formatCurrency(row.totalCost)}</td>
                                    <td
                                      className={
                                        row.profit > 0
                                          ? "text-success"
                                          : row.profit < 0
                                            ? "text-danger"
                                            : ""
                                      }
                                    >
                                      {formatCurrency(row.profit)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </PerfectScrollbar>
                        </div>
                      </div>
                    )}
                  </>
                )}
            </CardBody>
          </Card>

          {/* {results.calculated && !error && (
            <Card className="mb-4 shadow-sm">
              <CardHeader>
                <h2 className="h5 mb-0 mt-4">Biểu Đồ Điểm Hòa Vốn</h2>
              </CardHeader>
              <CardBody>
                <div className="chart-container" style={{ height: '400px', position: 'relative' }}>
                  <canvas ref={chartRef}></canvas>
                </div>
              </CardBody>
            </Card>
          )} */}
        </Col>
      </Row>
      {/* 
      <Card className="shadow-sm">
        <CardHeader>
          <h2 className="h5 mb-0 mt-4">Hướng Dẫn và Giải Thích</h2>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="6">
              <h5>Cách Tính Điểm Hòa Vốn</h5>
              <p>
                Điểm hòa vốn là điểm mà tại đó tổng doanh thu bằng tổng chi phí,
                tức là lợi nhuận bằng không.
              </p>
              <ul>
                <li>
                  <strong>Điểm hòa vốn (số lượng sản phẩm):</strong> <br />
                  <code>
                    Tổng chi phí cố định / (Giá bán đơn vị - Chi phí biến đổi
                    đơn vị)
                  </code>
                </li>
                <li>
                  <strong>Điểm hòa vốn (doanh thu):</strong> <br />
                  <code>Điểm hòa vốn (số lượng) × Giá bán đơn vị</code>
                </li>
              </ul>
            </Col>
            <Col md="6">
              <h5>Các Chỉ Số Quan Trọng</h5>
              <ul>
                <li>
                  <strong>Tổng chi phí cố định (Fixed Costs - FC):</strong> Chi
                  phí không thay đổi theo sản lượng (ví dụ: thuê nhà, lương cố
                  định).
                </li>
                <li>
                  <strong>
                    Chi phí biến đổi đơn vị (Variable Cost per Unit - VC/u):
                  </strong>{" "}
                  Chi phí thay đổi trực tiếp theo mỗi đơn vị sản phẩm (ví dụ:
                  nguyên vật liệu).
                </li>
                <li>
                  <strong>
                    Biên đóng góp đơn vị (Contribution Margin per Unit):
                  </strong>{" "}
                  <br /> <code>Giá bán đơn vị - Chi phí biến đổi đơn vị</code>.{" "}
                  <br />
                  Phần giá bán còn lại sau khi trừ chi phí biến đổi, dùng để bù
                  đắp chi phí cố định và tạo lợi nhuận.
                </li>
                <li>
                  <strong>Tỷ lệ đóng góp (Contribution Margin Ratio):</strong>{" "}
                  <br />{" "}
                  <code>(Biên đóng góp đơn vị / Giá bán đơn vị) × 100%</code>.
                </li>
              </ul>
            </Col>
          </Row>
        </CardBody>
      </Card> */}
    </Container>
  );
};

export default injectIntl(BreakEvenAnalysis);
